import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { PiTrashBold } from 'react-icons/pi';
import { useFieldArray, useWatch } from 'react-hook-form';

import { Badge } from '#/modules/commonComponents/badge';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import { cn } from '#/lib/utils';
import { draftGroupHeaderBarClassName } from '#/modules/momMgmt/workOrder/helper/draftGroupHeaderBarClassName';
import { getOutboundSourceTypeMeta } from '#/modules/momMgmt/workOrder/outbound/helper/outboundSourceTypeMeta';
import OutboundQtyStat from '#/modules/momMgmt/workOrder/outbound/components/OutboundQtyStat';
import { OUTBOUND_QTY_INPUT_CLASS } from '#/modules/momMgmt/workOrder/outbound/helper/outboundQtyStatClasses';
import { clampQtyToAvailable } from '#/utils/quantityBounds';

function SelectedMaterialGroup({
  group,
  open,
  highlighted,
  onToggleOpen,
  onFocusMaterial,
  register,
  errors,
  setValue,
  remove,
  disabled = false,
  groupRef,
  onAfterRemoveLine,
  onRemoveGroup,
}) {
  const totalQty = group.lines.reduce(
    (sum, item) => sum + (Number(item.line.qty) || 0),
    0,
  );
  const displayName = group.mtrName || group.mtrNo || '（未命名）';

  function handleRemove(index, mtrId) {
    const stillHasOther = group.lines.some((item) => item.index !== index);
    remove(index);
    if (!stillHasOther) {
      onAfterRemoveLine?.(mtrId);
    }
  }

  return (
    <li
      ref={groupRef}
      className={cn(
        'overflow-hidden rounded-md border border-slate-200 transition-colors',
        highlighted && 'border-primary-400 bg-primary-50 ring-2 ring-primary-200',
      )}
    >
      <div className={draftGroupHeaderBarClassName({ open, highlighted })}>
        <button
          type="button"
          className="
            flex min-w-0 flex-1 items-center gap-2 text-left transition-colors
            hover:text-primary-600
          "
          aria-expanded={open}
          onClick={onToggleOpen}
        >
          <ChevronDown
            size={18}
            className={cn(
              'shortcut_transition-transform shrink-0 origin-center text-gray-600',
              open ? 'rotate-0' : '-rotate-90',
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">
              {group.mtrName || '（未命名）'}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {group.mtrNo || group.mtrId}
            </p>
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <OutboundQtyStat label="已選" value={group.lines.length} valueWidth="count" />
          <OutboundQtyStat label="使用總量" value={totalQty} />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
            aria-label={`刪除 ${displayName} 群組`}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onRemoveGroup?.(group);
            }}
          >
            <PiTrashBold className="size-4" aria-hidden />
          </Button>
        </div>
      </div>

      <div
        className={cn(
          'grid shortcut_transition-disclosure',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          <ul className="m-0 divide-y divide-slate-100 list-none p-0">
            {group.lines.map(({ field, line, index }) => {
              const availableQty = Number(line.availableQty) || 0;
              const qtyMax = Math.max(1, availableQty || 1);
              const typeMeta = getOutboundSourceTypeMeta(line.sourceType);
              const sourceLabel = line.stockNo
                || line.invStockId
                || line.pendingAllocationId
                || '—';
              const fieldId = `lines.${index}.qty`;
              const fieldError = getNestedError(errors, fieldId);

              return (
                <li
                  key={field.id}
                  className={cn(
                    'cursor-pointer px-3 py-2.5 grid items-center gap-2 transition-colors',
                    'grid-cols-[minmax(0,1fr)_auto]',
                    'hover:bg-slate-50',
                  )}
                  onClick={() => onFocusMaterial?.(group.mtrId)}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-mono text-sm text-slate-700">
                        {sourceLabel}
                      </span>
                      <Badge
                        variant={typeMeta.variant}
                        className={typeMeta.badgeClassName}
                      >
                        {typeMeta.shortLabel}
                      </Badge>
                    </div>
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-2"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <OutboundQtyStat
                      inline
                      label="可用"
                      value={line.availableQty ?? '—'}
                    />
                    <Input
                      id={fieldId}
                      type="number"
                      min={1}
                      max={qtyMax}
                      disabled={disabled}
                      aria-label={`${sourceLabel} 使用數量`}
                      aria-invalid={fieldError ? true : undefined}
                      className={OUTBOUND_QTY_INPUT_CLASS}
                      {...register(fieldId, {
                        onBlur: (event) => {
                          const next = clampQtyToAvailable(
                            event.target.value,
                            availableQty,
                          );
                          setValue(fieldId, next, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        },
                      })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label={`移除 ${sourceLabel}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemove(index, group.mtrId);
                      }}
                      disabled={disabled}
                    >
                      <PiTrashBold className="size-4" aria-hidden />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </li>
  );
}

SelectedMaterialGroup.propTypes = {
  group: PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.object,
      line: PropTypes.object,
      index: PropTypes.number,
    })),
  }).isRequired,
  open: PropTypes.bool.isRequired,
  highlighted: PropTypes.bool,
  onToggleOpen: PropTypes.func.isRequired,
  onFocusMaterial: PropTypes.func,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  groupRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onAfterRemoveLine: PropTypes.func,
  onRemoveGroup: PropTypes.func,
};

const WorkOrderOutboundSelectedPanel = ({
  control,
  register,
  errors,
  setValue,
  disabled = false,
  focusedMtrId = null,
  focusToken = 0,
  onFocusMaterial,
  collapsedByMtrId = {},
  onToggleGroup,
  onAfterRemoveLine,
}) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const { fields, remove } = useFieldArray({
    control,
    name: 'lines',
  });
  const lines = useWatch({ control, name: 'lines', defaultValue: [] });
  const groupRefs = useRef({});

  const groups = useMemo(() => {
    const order = [];
    const map = new Map();

    fields.forEach((field, index) => {
      const line = lines?.[index] ?? {};
      const mtrId = line.mtrId || field.mtrId || `unknown-${index}`;
      if (!map.has(mtrId)) {
        map.set(mtrId, {
          mtrId,
          mtrNo: line.mtrNo,
          mtrName: line.mtrName,
          lines: [],
        });
        order.push(mtrId);
      }
      map.get(mtrId).lines.push({ field, line, index });
    });

    return order.map((mtrId) => map.get(mtrId));
  }, [fields, lines]);

  useEffect(() => {
    if (!focusedMtrId) {
      return;
    }
    const node = groupRefs.current[focusedMtrId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedMtrId, focusToken]);

  function isGroupOpen(mtrId) {
    return collapsedByMtrId[mtrId] !== true;
  }

  async function handleRemoveGroup(group) {
    const name = group.mtrName || group.mtrNo || '（未命名）';
    const ok = await confirm({
      title: '刪除群組',
      message: `是否確定刪除${name} 群組？`,
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }

    const nextLines = (lines ?? []).filter((line) => line.mtrId !== group.mtrId);
    setValue('lines', nextLines, { shouldDirty: true, shouldValidate: true });
    onAfterRemoveLine?.(group.mtrId);
  }

  if (fields.length === 0) {
    return (
      <>
        <p className="px-1 py-8 text-center text-sm text-muted-foreground">
          請自左欄「選擇來源」加入已選取。
        </p>
        {ConfirmDialogHost}
      </>
    );
  }

  return (
    <>
      <ul className="m-0 list-none space-y-3 p-0">
        {groups.map((group) => (
          <SelectedMaterialGroup
            key={group.mtrId}
            group={group}
            open={isGroupOpen(group.mtrId)}
            highlighted={focusedMtrId === group.mtrId}
            onToggleOpen={() => onToggleGroup?.(group.mtrId)}
            onFocusMaterial={onFocusMaterial}
            register={register}
            errors={errors}
            setValue={setValue}
            remove={remove}
            disabled={disabled}
            onAfterRemoveLine={onAfterRemoveLine}
            onRemoveGroup={handleRemoveGroup}
            groupRef={(node) => {
              if (node) {
                groupRefs.current[group.mtrId] = node;
              } else {
                delete groupRefs.current[group.mtrId];
              }
            }}
          />
        ))}
      </ul>
      {ConfirmDialogHost}
    </>
  );
};

WorkOrderOutboundSelectedPanel.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  focusedMtrId: PropTypes.string,
  focusToken: PropTypes.number,
  onFocusMaterial: PropTypes.func,
  collapsedByMtrId: PropTypes.objectOf(PropTypes.bool),
  onToggleGroup: PropTypes.func,
  onAfterRemoveLine: PropTypes.func,
};

export default WorkOrderOutboundSelectedPanel;
